import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import "../../scss/news.scss";
import { useI18n } from "../../i18n/I18nProvider";

interface Post {
  id: number;
  title_pl: string;
  desc_pl: string;
  title_eng: string;
  desc_eng: string;
  author: string;
  is_public: boolean;
  created_at: string;
}

const SingleNewsComponent: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t, language } = useI18n();
  const [post, setPost] = useState<Post | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [imageUrl, setImageUrl] = useState<string>("/post_backup.png");

  const API_URL = `${process.env.REACT_APP_BASE_URL}/v2/main_page/posts/public`;

  useEffect(() => {
    const fetchPost = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await fetch(`${API_URL}?page=1&page_size=100`);

        if (!response.ok) {
          throw new Error(t("news_error_fetch_post"));
        }

        const data = await response.json();
        const foundPost = data.posts.find(
          (p: Post) => p.id === parseInt(id || "0")
        );

        if (!foundPost) {
          throw new Error(t("news_post_not_found"));
        }

        setPost(foundPost);

        const postImageUrl = `https://cybernews.wsiz.edu.pl/images/cc_posts/${foundPost.id}.png`;
        const imgCheck = new Image();
        imgCheck.onload = () => setImageUrl(postImageUrl);
        imgCheck.onerror = () => setImageUrl("/post_backup.png");
        imgCheck.src = postImageUrl;
      } catch (err) {
        setError(err instanceof Error ? err.message : t("news_error_generic"));
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchPost();
    }
  }, [id]);

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    const dateStr = date.toLocaleDateString(language === "en" ? "en-GB" : "pl-PL", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
    const timeStr = date.toLocaleTimeString(language === "en" ? "en-GB" : "pl-PL", {
      hour: "2-digit",
      minute: "2-digit",
    });
    return `${dateStr} o ${timeStr}`;
  };

  const goBack = () => {
    navigate("/aktualnosci");
  };

  if (loading) {
    return (
      <section className="news-section">
        <div className="news-container">
          <div className="news-loading" data-aos="fade-up">
            <div className="spinner"></div>
            <p>{t("news_loading")}</p>
          </div>
        </div>
      </section>
    );
  }

  if (error || !post) {
    return (
      <section className="news-section">
        <div className="news-container">
          <div className="news-error" data-aos="fade-up">
            <p>&#x26A0;&#xFE0F; {error || t("news_post_not_found")}</p>
            <button
              className="single-news-back-btn"
              onClick={goBack}
              aria-label={t("news_back_label")}
              style={{ marginTop: "1.5rem" }}
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M19 12H5" />
                <path d="m12 19-7-7 7-7" />
              </svg>
              {t("news_back_text")}
            </button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="news-section">
      <div className="news-container">

        <article className="single-news-article" data-aos="fade-up">
          <div className="single-news-topbar">
            <button
              className="single-news-back-btn"
              onClick={goBack}
              aria-label={t("news_back_label")}
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M19 12H5" />
                <path d="m12 19-7-7 7-7" />
              </svg>
              {t("news_back_short")}
            </button>
          </div>

          <div className="single-news-hero-image">
            <img
              src={imageUrl}
              alt={language === "en" && post.title_eng ? post.title_eng : post.title_pl}
              className="hero-image"
            />
          </div>

          <div className="single-news-header">
            <h1 className="single-news-title">{language === "en" && post.title_eng ? post.title_eng : post.title_pl}</h1>
            <div className="single-news-meta">
              <div className="news-author">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                  <circle cx="12" cy="7" r="4"></circle>
                </svg>
                <span>{t("news_author_prefix")}<strong>{post.author}</strong></span>
              </div>
              <div className="news-date-time">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"></circle>
                  <polyline points="12 6 12 12 16 14"></polyline>
                </svg>
                <time>{formatDate(post.created_at)}</time>
              </div>
            </div>
          </div>

          <div className="single-news-content">
            <ReactMarkdown>{language === "en" && post.desc_eng ? post.desc_eng : post.desc_pl}</ReactMarkdown>
          </div>

          <div className="single-news-bottom-nav">
            <button
              className="single-news-back-btn"
              onClick={goBack}
              aria-label={t("news_back_label")}
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M19 12H5" />
                <path d="m12 19-7-7 7-7" />
              </svg>
              {t("news_back_text")}
            </button>
          </div>
        </article>
      </div>
    </section>
  );
};

export default SingleNewsComponent;
